// src/services/fileService.js
//
// Servicio para gestionar archivos contra el FilesController del backend.
// Endpoints cubiertos:
// - POST   /api/files                 -> upload
// - GET    /api/files/{id}/stream     -> download/stream (blob)
// - DELETE /api/files/{id}            -> delete
//
// Requisitos previos:
// - axiosInstance configurado con baseURL y auth

import axiosInstance from "../config/axiosInstance";

const BASE_URL = "/files";

/**
 * Sube un archivo al almacenamiento configurado (Local o Azure).
 * El backend requiere: file (IFormFile), module (string), referenceType (string).
 *
 * @param {Object} params
 * @param {File} params.file - Archivo a subir (IFormFile)
 * @param {string} params.module - Módulo lógico (ej. "legal", "company", "lawyer")
 * @param {string} params.referenceType - Tipo de referencia (ej. "lawyer-photo", "lawyer-attachment")
 * @returns {Promise<StoredFileDto>} StoredFileDto con { id, fileName, contentType, size, ... }
 */
export const uploadFile = async ({ file, module, referenceType }) => {
  if (!file) throw new Error("El archivo es obligatorio.");
  if (!module) throw new Error("El módulo es obligatorio.");
  if (!referenceType) throw new Error("El tipo de referencia es obligatorio.");

  const formData = new FormData();
  formData.append("file", file);
  formData.append("module", module);
  formData.append("referenceType", referenceType);

  const { data } = await axiosInstance.post(`${BASE_URL}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return data;
};

/**
 * Sube múltiples archivos (azúcar sintáctico). Ejecuta uploadFile por cada archivo.
 * @param {Object} params
 * @param {File[]} params.files
 * @param {string} params.module
 * @param {string} params.referenceType
 * @returns {Promise<StoredFileDto[]>}
 */
export const uploadFiles = async ({ files = [], module, referenceType }) => {
  const tasks = files.map((file) =>
    uploadFile({ file, module, referenceType })
  );
  return Promise.all(tasks);
};

/**
 * Descarga/obtiene el stream del archivo por id. Retorna un Blob y nombre sugerido.
 * Útil para descargar o previsualizar (PDF, imágenes, etc).
 *
 * @param {string} id - ID del archivo (Guid)
 * @returns {Promise<{ blob: Blob, fileName: string, contentType: string }>}
 */
export const downloadFile = async (id) => {
  const resp = await axiosInstance.get(`${BASE_URL}/${id}/stream`, {
    responseType: "blob", // importante para obtener un Blob
  });

  // Intentar extraer fileName del header "content-disposition"
  let fileName = "archivo";
  const dispo = resp.headers?.["content-disposition"];
  if (dispo) {
    const match = dispo.match(
      /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i
    );
    fileName = decodeURIComponent(match?.[1] || match?.[2] || fileName);
  }

  const contentType =
    resp.headers?.["content-type"] || "application/octet-stream";
  return { blob: resp.data, fileName, contentType };
};

/**
 * Abre el archivo en una nueva pestaña (si el navegador lo soporta).
 * Internamente usa downloadFile para crear un ObjectURL temporal.
 * @param {string} id
 */
export const openFileInNewTab = async (id) => {
  const { blob } = await downloadFile(id);
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  // Opcional: liberar URL después de unos segundos
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/**
 * Descarga el archivo forzando el “save as”.
 * @param {string} id
 */
export const saveFile = async (id) => {
  const { blob, fileName } = await downloadFile(id);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName || "archivo";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(link.href), 60_000);
};

/**
 * Elimina un archivo por su Id.
 * @param {string} id - ID del archivo (Guid)
 * @returns {Promise<void>}
 */
export const deleteFile = async (id) => {
  await axiosInstance.delete(`${BASE_URL}/${id}`);
};
